import { Inject, Injectable } from '@angular/core';
import { FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { PersonaType } from '@vendasta/iam';
import {
  ConversationInterface,
  DomainKeyValue,
  KeyValueTypes,
  SearchTaskRequest,
  SearchTaskRequestInterface,
  SearchTaskResponseInterface,
  TaskInterface,
} from '@vendasta/task';
import {
  CommentInterface,
  CommentProjectionFilter,
  CommentType,
  LoadCommentsRequest,
  TaskManagerSdkService,
} from '@vendasta/task-manager';
import { EMPTY, forkJoin, iif, Observable, of } from 'rxjs';
import { catchError, expand, map, retry, scan, shareReplay, switchMap, take } from 'rxjs/operators';
import { ProjectTrackerServiceInterface, ProjectTrackerServiceInterfaceToken } from '../../project-tracker-interface';
import {
  DEPENDENT_TAG,
  FEATURE_FLAG_LIST,
  FULFILLMENT_ORDER_ID_METADATA_KEY,
  MILESTONE_APPROVED_TAG,
  ORDER_ID_METADATA_KEY,
  PROJECT_TYPE,
  TASK_DATA_FEEDBACK_KEY,
  VISIBLE_TAG,
} from '../constants';
import { ProjectTrackerInfo, Subtask } from '../interfaces';

const PAGE_SIZE = 250;

@Injectable()
export class ProjectsAndSubtasksByProductsService {
  constructor(
    private taskManagerSdkService: TaskManagerSdkService,
    private featureFlagService: FeatureFlagService,
    @Inject(ProjectTrackerServiceInterfaceToken) private projectTrackerService: ProjectTrackerServiceInterface,
  ) {}

  static tasksToSubtasks(tasks: TaskInterface[], uploadingPersonaTypes: string[]): Subtask[] {
    return tasks.map((task) => {
      if (task.tags?.includes(DEPENDENT_TAG)) {
        task.dueDate = null;
      }
      const feedbackData = DomainKeyValue.fromKeyValue(task.data).get(TASK_DATA_FEEDBACK_KEY, KeyValueTypes.BOOL);
      const feedbackEnabled = feedbackData.length > 0 ? (feedbackData[0] as boolean) : false;
      const metadata = DomainKeyValue.fromKeyValue(task.metadata);
      const partnerId = metadata.get('partner', KeyValueTypes.STRING).toString();
      const accountGroupId = metadata.get('account-group', KeyValueTypes.STRING).toString();
      const id = task.identity.parentPath + task.identity.taskId;
      const subtask: Subtask = {
        title: task.title,
        dueDate: task.dueDate,
        completionDate: task.completionDate,
        status: task.status,
        notes: task.notes,
        feedbackEnabled: feedbackEnabled,
        partnerId: partnerId,
        accountGroupId: accountGroupId,
        subtaskId: id,
        files: task.attachment?.filter((a) => uploadingPersonaTypes.includes(a.uploadingPersonaType)),
        approved: task.tags?.includes(MILESTONE_APPROVED_TAG),
        showFeedback: false,
        conversations: task.conversations?.filter((c) => c?.isPublic && !this.isUserMentionConversation(c.message)),
        tags: task?.tags ?? [],
      };
      return subtask;
    });
  }

  private static isVisible(task: TaskInterface): boolean {
    return task?.tags?.includes(VISIBLE_TAG);
  }

  private static isUserMentionConversation(conversation: string): boolean {
    return conversation.includes('data-mention=');
  }

  private mapInboxConversation(conversation: CommentInterface): ConversationInterface {
    return {
      sendFrom: conversation.senderName,
      message: conversation.messageText,
      created: conversation.messageCreated,
      source: conversation.externalMessageId,
      isPublic: conversation.commentType === CommentType.COMMENT_TYPE_PUBLIC,
    } as ConversationInterface;
  }

  private searchTasks(searchRequest: SearchTaskRequestInterface): Observable<TaskInterface[]> {
    return this.taskManagerSdkService.search(new SearchTaskRequest(searchRequest)).pipe(
      expand((resp: SearchTaskResponseInterface) =>
        iif(
          () => resp.hasMore,
          this.taskManagerSdkService.search(new SearchTaskRequest({ ...searchRequest, cursor: resp.nextCursor })),
          EMPTY,
        ),
      ),
      scan((acc: TaskInterface[], curr: SearchTaskResponseInterface) => {
        if (curr?.tasks?.length > 0) {
          return acc.concat(curr.tasks);
        }
        return acc;
      }, []),
    );
  }

  private getProjectTrackerInfo(
    project: TaskInterface,
    conversations: ConversationInterface[],
    uploadingPersonaTypes: PersonaType[],
    smbVisibleOnly: boolean,
    excludeDeleted = true,
  ): Observable<ProjectTrackerInfo> {
    return this.taskManagerSdkService
      .getTaskMulti({ identities: project.subtasks, excludeDeleted: excludeDeleted })
      .pipe(
        map((subtasks) => {
          const st = ProjectsAndSubtasksByProductsService.tasksToSubtasks(subtasks, uploadingPersonaTypes);
          if (smbVisibleOnly) {
            return st.filter((s) => ProjectsAndSubtasksByProductsService.isVisible(s));
          }
          return st;
        }),
        map((subtasks) => {
          return {
            projectTitle: project.title,
            projectIdentity: project.identity,
            projectConversations: conversations.sort((a, b) => b.created?.getTime() - a.created?.getTime()),
            projectStatus: project.status,
            subtasks: subtasks,
          } as ProjectTrackerInfo;
        }),
      );
  }

  private getProjectConversations(project: TaskInterface): Observable<ConversationInterface[]> {
    return this.taskManagerSdkService
      .loadComments(
        new LoadCommentsRequest({
          identity: project.identity,
          projectionFilter: new CommentProjectionFilter({ retrievePrivate: false, retrieveSubtasks: true }),
        }),
      )
      .pipe(
        map((response) => response?.messages || []),
        map((conversations) => conversations?.map(this.mapInboxConversation)),
      );
  }

  private searchAllProjectsAndConvertToProjectTrackerInfo(
    searchRequest: SearchTaskRequestInterface,
    uploadingPersonaTypes: PersonaType[],
  ): Observable<ProjectTrackerInfo[]> {
    const smbVisibleOnly = searchRequest.tags?.includes(VISIBLE_TAG);

    return this.searchTasks(searchRequest).pipe(
      map((projects) =>
        projects.map((p) =>
          this.getProjectConversations(p).pipe(
            take(1),
            switchMap((conversations) =>
              this.getProjectTrackerInfo(p, conversations, uploadingPersonaTypes, smbVisibleOnly),
            ),
          ),
        ),
      ),
      switchMap((projects) => forkJoin(projects)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  lookupVisibleProjectsForAccountByOrderIds(
    partnerId: string,
    accountGroupId: string,
    orderIds: string[],
    uploadingPersonaTypes: PersonaType[],
  ): Observable<ProjectTrackerInfo[]> {
    const searchRequest: SearchTaskRequestInterface = {
      namespace: `partner/${partnerId}/account-group/${accountGroupId}`,
      types: [PROJECT_TYPE],
      tags: [VISIBLE_TAG],
      metadata: new DomainKeyValue().set(ORDER_ID_METADATA_KEY, orderIds, KeyValueTypes.STRING).toKeyValue(),
      pageSize: PAGE_SIZE,
    };
    return this.searchAllProjectsAndConvertToProjectTrackerInfo(searchRequest, uploadingPersonaTypes);
  }

  lookupVisibleProjectForAccountByWorkOrderIds(
    partnerId: string,
    accountGroupId: string,
    workOrderIds: string[],
    uploadingPersonaTypes: PersonaType[],
  ): Observable<ProjectTrackerInfo[]> {
    return this.lookupProjectsForAccountByWorkOrderIds(
      partnerId,
      accountGroupId,
      workOrderIds,
      false,
      uploadingPersonaTypes,
    );
  }

  lookupProjectsForAccountByWorkOrderIds(
    partnerId: string,
    accountGroupId: string,
    workOrderIds: string[],
    smbVisibleOnly: boolean,
    uploadingPersonaTypes: PersonaType[],
  ): Observable<ProjectTrackerInfo[]> {
    const searchRequest: SearchTaskRequestInterface = {
      namespace: `partner/${partnerId}/account-group/${accountGroupId}`,
      types: [PROJECT_TYPE],
      metadata: new DomainKeyValue()
        .set(FULFILLMENT_ORDER_ID_METADATA_KEY, workOrderIds, KeyValueTypes.STRING)
        .toKeyValue(),
      pageSize: PAGE_SIZE,
    };
    if (smbVisibleOnly) {
      searchRequest.tags = [VISIBLE_TAG];
    }
    return this.searchAllProjectsAndConvertToProjectTrackerInfo(searchRequest, uploadingPersonaTypes);
  }

  featureFlags(partnerId: string): Observable<FeatureFlagStatusInterface> {
    return this.featureFlagService.batchGetStatus(partnerId, '', FEATURE_FLAG_LIST).pipe(
      retry(2),
      catchError(() => {
        const flags: FeatureFlagStatusInterface = {};
        FEATURE_FLAG_LIST.forEach((f) => (flags[f] = false));
        return of(flags);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }
}
